import { Palette } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuLabel, DropdownMenuRadioGroup, DropdownMenuRadioItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { useTheme } from '@/hooks/use-theme';
import { THEMES } from '@/lib/themes';

/** Three dots of the theme's own colours: background, surface, accent. */
function Swatch(props: { readonly colours: readonly [string, string, string] }) {
  return (
    <span className="flex shrink-0 overflow-hidden rounded-full ring-1 ring-white/15" aria-hidden>
      {props.colours.map((colour) => (
        <span key={colour} className="size-3.5" style={{ backgroundColor: colour }} />
      ))}
    </span>
  );
}

/** Picks the look; it applies at once and is remembered in this browser. */
export function ThemeSelector() {
  const { theme, setTheme } = useTheme();
  const current = THEMES.find((candidate) => candidate.id === theme) ?? THEMES[0];

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" aria-label={`Theme: ${current.name}`} title={`Theme: ${current.name}`}>
          <Palette className="size-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-72">
        <DropdownMenuLabel>Theme</DropdownMenuLabel>
        <DropdownMenuRadioGroup value={theme} onValueChange={(value) => setTheme(THEMES.find((candidate) => candidate.id === value)?.id ?? current.id)}>
          {THEMES.map((candidate) => (
            <DropdownMenuRadioItem key={candidate.id} value={candidate.id} className="items-start gap-3 py-2">
              <Swatch colours={candidate.swatches} />
              <span className="min-w-0">
                <span className="block text-sm font-medium">{candidate.name}</span>
                <span className="block text-xs text-muted-foreground">{candidate.description}</span>
              </span>
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
